import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CustomButton from '../OnboardingComponents/CustomButton';
import { ICONS } from '../../constants/icons';

const SESSION_OPTIONS = [
  { id: 'solo', label: 'Solo Session' },
  { id: 'shadow', label: 'Shadow Session' },
];

/**
 * Start Session trigger. Button chrome stays the same; click opens
 * Solo / Shadow and reports the chosen type.
 *
 * @param {'auto'|'top'|'bottom'} menuPlacement
 */
const StartSessionDropdown = ({
  label = 'Start Session',
  playIconSrc = ICONS.play,
  className = '',
  variant = 'secondary',
  size = 'sm',
  fullWidth = false,
  skipDropdown = false,
  menuPlacement = 'auto',
  disabled = false,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onScroll = () => setOpen(false);

    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const handleClick = (event) => {
    event.stopPropagation();
    if (disabled) return;
    if (skipDropdown) {
      onSelect?.();
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 160);
    const menuHeight = 96;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward =
      menuPlacement === 'top' ||
      (menuPlacement === 'auto' && spaceBelow < menuHeight + 8);

    setCoords({
      top: openUpward ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
      left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      width,
      openUpward,
    });
    setOpen(true);
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`project-session-start${fullWidth ? ' project-session-start--full' : ''}`}
      >
        <CustomButton
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          trailingIconSrc={playIconSrc}
          trailingIconAlt=""
          className={className}
          disabled={disabled}
          onClick={handleClick}
          aria-haspopup={skipDropdown || disabled ? undefined : 'menu'}
          aria-expanded={skipDropdown || disabled ? undefined : open}
        >
          {label}
        </CustomButton>
      </span>
      {open && coords && !skipDropdown
        ? createPortal(
            <ul
              ref={menuRef}
              className={`project-session-start__menu${
                coords.openUpward ? ' project-session-start__menu--up' : ''
              }`}
              role="menu"
              style={{ top: coords.top, left: coords.left, minWidth: coords.width }}
            >
              {SESSION_OPTIONS.map((option) => (
                <li key={option.id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="project-session-start__option"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpen(false);
                      onSelect?.(option.id);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            document.body
          )
        : null}
    </>
  );
};

export default StartSessionDropdown;
